/**
 * EnvironmentBanner - Shows current environment and backend configuration
 * 
 * P0 ENVIRONMENT INTEGRITY: Users must always know what environment they're on.
 * Prevents testing the wrong system.
 */

import React, { useState, useEffect } from 'react';
import { AlertTriangle, Server, CheckCircle } from 'lucide-react';
import { BACKEND_URL, IS_PRODUCTION } from '../config/api';

const getEnvironmentInfo = () => {
  const hostname = window.location.hostname;
  
  // Determine frontend environment
  let frontendEnv = 'UNKNOWN';
  if (hostname === 'www.newaudia.com' || hostname === 'newaudia.com') {
    frontendEnv = 'PRODUCTION';
  } else if (hostname.includes('preview.emergentagent.com') || hostname.includes('emergent.host') || hostname.includes('emergentagent.com')) {
    frontendEnv = 'PREVIEW';
  } else if (hostname === 'localhost' || hostname === '127.0.0.1') {
    frontendEnv = 'LOCAL';
  }
  
  // Determine backend environment
  // On production (same-origin), BACKEND_URL is empty - this means backend matches frontend
  let backendEnv = 'UNKNOWN';
  let backendHost = 'same-origin';
  
  if (BACKEND_URL === '' || !BACKEND_URL) {
    // Same-origin mode - backend environment matches frontend
    backendEnv = frontendEnv;
    backendHost = hostname;
  } else {
    // Explicit backend URL - parse it
    try {
      backendHost = new URL(BACKEND_URL).hostname;
      if (backendHost === 'www.newaudia.com' || backendHost === 'newaudia.com') {
        backendEnv = 'PRODUCTION';
      } else if (backendHost.includes('preview.emergentagent.com') || backendHost.includes('emergent.host') || backendHost.includes('emergentagent.com')) {
        backendEnv = 'PREVIEW';
      } else if (backendHost === 'localhost' || backendHost === '127.0.0.1') {
        backendEnv = 'LOCAL';
      }
    } catch (e) {
      console.warn('[EnvironmentBanner] Could not parse BACKEND_URL:', BACKEND_URL);
    }
  }
  
  // Check for environment mismatch (CRITICAL ERROR)
  // Only block if one is PRODUCTION and the other is not
  const isMismatch = (frontendEnv === 'PRODUCTION' && backendEnv !== 'PRODUCTION') ||
                     (backendEnv === 'PRODUCTION' && frontendEnv !== 'PRODUCTION');
  
  return {
    frontendEnv,
    backendEnv,
    backendHost,
    isMismatch,
    isProduction: frontendEnv === 'PRODUCTION' && backendEnv === 'PRODUCTION'
  };
};

export const EnvironmentBanner = () => {
  const [envInfo, setEnvInfo] = useState(null);
  const [dismissed, setDismissed] = useState(false);
  
  useEffect(() => {
    setEnvInfo(getEnvironmentInfo());
  }, []);
  
  if (!envInfo || dismissed) return null;
  
  // Critical mismatch - block the app
  if (envInfo.isMismatch) {
    return (
      <div className="fixed inset-0 z-[9999] bg-red-900/95 flex items-center justify-center p-8">
        <div className="bg-red-950 border-2 border-red-500 rounded-xl p-8 max-w-lg text-center">
          <AlertTriangle className="w-16 h-16 text-red-400 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-red-200 mb-4">Configuration Error</h1>
          <p className="text-red-300 mb-4">
            Frontend is on <strong>{envInfo.frontendEnv}</strong> but backend is on <strong>{envInfo.backendEnv}</strong>.
          </p>
          <p className="text-red-400 text-sm">
            This is a critical misconfiguration. Please contact support.
          </p>
          <div className="mt-6 p-3 bg-red-900/50 rounded-lg text-xs text-red-400 font-mono">
            Backend: {envInfo.backendHost}
          </div>
        </div>
      </div>
    );
  }
  
  // Production - no badge needed (clean UI for end users)
  if (envInfo.isProduction) {
    return null;
  }
  
  // Preview/Local - show warning banner (helps developers know they're not on production)
  return (
    <div className="fixed top-2 left-2 z-50">
      <div 
        className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-amber-900/90 border border-amber-600/50 cursor-pointer hover:bg-amber-800/90 transition-colors"
        onClick={() => setDismissed(true)}
        title="Click to dismiss"
      >
        <Server className="w-3.5 h-3.5 text-amber-400" />
        <div className="text-[10px]">
          <span className="font-semibold text-amber-300">{envInfo.frontendEnv}</span>
          <span className="text-amber-400/70 ml-1.5">→ {envInfo.backendHost.slice(0, 25)}</span>
        </div>
      </div>
    </div>
  );
};

export default EnvironmentBanner;
